function parseYear(val) {
  if (val == null || val === '') return 0;
  const n = parseInt(String(val).slice(0, 4), 10);
  return Number.isNaN(n) ? 0 : n;
}

/** Low-value trailing tokens safe to drop when shortening pill labels. */
const DROP_TRAILING = new Set([
  'certificate',
  'certification',
  'certifications',
  'certified',
  'award',
  'awards',
  'recognition',
  'recognitions',
  'programme',
  'program',
  'course',
]);

/**
 * Fixed issuer → short platform aliases for cert pills.
 * Only known strings; never invent a platform when unmapped.
 */
const ISSUER_ALIASES = [
  { re: /\bamazon\s+web\s+services\b|\baws\b/i, short: 'AWS' },
  { re: /\bgoogle\s+cloud\b|\bgcp\b|\bgoogle\b/i, short: 'Google' },
  { re: /\bmicrosoft\s+azure\b|\bazure\b/i, short: 'Azure' },
  { re: /\bmicrosoft\b/i, short: 'Microsoft' },
  { re: /\bintel\b/i, short: 'Intel' },
  { re: /\bcncf\b|\bcloud\s+native\s+computing\s+foundation\b/i, short: 'CNCF' },
  { re: /\bcisco\b/i, short: 'Cisco' },
  { re: /\boracle\b/i, short: 'Oracle' },
  { re: /\bibm\b/i, short: 'IBM' },
  { re: /\bmeta\b|\bfacebook\b/i, short: 'Meta' },
  { re: /\bcomptia\b/i, short: 'CompTIA' },
  { re: /\bhashicorp\b/i, short: 'HashiCorp' },
  { re: /\bsalesforce\b/i, short: 'Salesforce' },
  { re: /\bred\s*hat\b/i, short: 'Red Hat' },
];

export function shortIssuerAlias(issuedBy) {
  const s = String(issuedBy || '').trim();
  if (!s) return null;
  for (const { re, short } of ISSUER_ALIASES) {
    if (re.test(s)) return short;
  }
  return null;
}

/**
 * Non-AI abbreviate for profile credential pills (max 5 words).
 * Drops low-value trailing tokens before truncating; avoids mid-phrase ellipsis
 * when a clean word-bounded short form fits.
 */
export function shortBadgeLabel(text, maxWords = 5, maxChars = 52) {
  let s = String(text || '').trim().replace(/\s+/g, ' ');
  if (!s) return '';

  // Strip decorative separators that waste word budget.
  s = s.replace(/\s*[—–|·•]\s*/g, ' ').replace(/\s+/g, ' ').trim();

  let words = s.split(' ').filter(Boolean);

  // Always drop low-value trailing tokens (even under maxWords).
  while (words.length > 1) {
    const last = words[words.length - 1].toLowerCase().replace(/[^a-z]/g, '');
    if (!DROP_TRAILING.has(last)) break;
    words = words.slice(0, -1);
  }

  if (words.length > maxWords) {
    words = words.slice(0, maxWords);
  }

  let out = words.join(' ');
  if (out.length > maxChars) {
    // Prefer whole-word cut under maxChars rather than mid-word ellipsis.
    const fitted = [];
    let len = 0;
    for (const w of words) {
      const next = fitted.length ? len + 1 + w.length : w.length;
      if (next > maxChars) break;
      fitted.push(w);
      len = next;
    }
    out = fitted.length ? fitted.join(' ') : words[0].slice(0, maxChars);
  }
  return out;
}

/** Build a cert pill label: optional short platform + condensed name (≤5 words). */
export function buildCertBadgeLabel(name, issuedBy) {
  const rawName = String(name || '').trim().replace(/\s+/g, ' ');
  if (!rawName) return '';

  const platform = shortIssuerAlias(issuedBy);
  const nameLower = rawName.toLowerCase();
  const platformAlreadyInName = platform
    && nameLower.split(/\s+/).some((w) => w.replace(/[^a-z0-9]/gi, '').toLowerCase() === platform.toLowerCase());

  if (platform && !platformAlreadyInName) {
    // Prefer "AWS Solutions Architect" over repeating long issuer in name.
    const condensed = shortBadgeLabel(rawName, 4);
    const combined = `${platform} ${condensed}`.trim();
    return shortBadgeLabel(combined, 5);
  }

  return shortBadgeLabel(rawName, 5);
}

/** Award pill: title, optionally + short issuer if it fits in 5 words. */
export function buildAwardBadgeLabel(title, issuedBy) {
  const raw = String(title || '').trim().replace(/\s+/g, ' ');
  if (!raw) return '';
  const base = shortBadgeLabel(raw, 5);
  const issuer = shortIssuerAlias(issuedBy) || null;
  if (!issuer) return base;

  const baseWords = base.split(' ').filter(Boolean);
  if (baseWords.length >= 5) return base;
  if (base.toLowerCase().includes(issuer.toLowerCase())) return base;

  const withIssuer = `${base} ${issuer}`;
  if (withIssuer.split(' ').filter(Boolean).length <= 5) {
    return shortBadgeLabel(withIssuer, 5);
  }
  return base;
}

/** Non-empty Key Highlights bullets from company resume payload (max 3 on report). */
export function pickKeyHighlights(payload) {
  const raw = payload?.key_highlights;
  if (!Array.isArray(raw)) return [];
  return raw
    .map((h) => (typeof h === 'string' ? h.trim() : ''))
    .filter(Boolean)
    .slice(0, 3);
}

/** Title line under the candidate name on Profile Summary. */
export function pickProfileTitle(payload, candidate) {
  const headline = payload?.header?.headline;
  if (typeof headline === 'string' && headline.trim()) return headline.trim();
  const parts = [candidate?.role_label, candidate?.department_label].filter(Boolean);
  return parts.length ? parts.join(' · ') : null;
}

function sortByYearDesc(items) {
  return [...items].sort((a, b) => parseYear(b.year) - parseYear(a.year));
}

/**
 * Short badge pills for Profile Summary (max 2).
 * Prefer certifications → awards/achievements → highest qualification.
 * Exclusive tiers — never mix kinds. Icons are keyed off `kind` in the UI.
 */
export function listCertBadges(payload) {
  const certs = Array.isArray(payload?.certifications) ? payload.certifications : [];
  const fromCerts = sortByYearDesc(
    certs.filter((c) => c && typeof c === 'object' && c.name),
  )
    .map((c) => ({
      name: buildCertBadgeLabel(c.name, c.issued_by),
      kind: 'certification',
      issued_by: c.issued_by ? String(c.issued_by).trim() : null,
      year: c.year ? String(c.year).trim() : null,
    }))
    .filter((c) => c.name);

  if (fromCerts.length) return fromCerts.slice(0, 2);

  const awards = Array.isArray(payload?.awards) ? payload.awards : [];
  const fromAwards = sortByYearDesc(
    awards.filter((a) => a && typeof a === 'object' && (a.name || a.title)),
  )
    .map((a) => {
      const raw = a.name || a.title;
      const issued = a.issued_by || a.issuer || null;
      return {
        name: buildAwardBadgeLabel(raw, issued),
        kind: 'award',
        issued_by: issued ? String(issued).trim() : null,
        year: a.year ? String(a.year).trim() : null,
      };
    })
    .filter((c) => c.name);

  if (fromAwards.length) return fromAwards.slice(0, 2);

  const edu = Array.isArray(payload?.education) ? payload.education : [];
  const sorted = sortByYearDesc(
    edu.filter((e) => e && typeof e === 'object' && (e.degree || e.institution)),
  );
  if (!sorted.length) return [];

  const e = sorted[0];
  const degree = e.degree ? String(e.degree).trim() : '';
  const institution = e.institution ? String(e.institution).trim() : '';
  const label = degree || institution;
  return [{
    name: shortBadgeLabel(label, 5),
    kind: 'education',
    issued_by: degree && institution ? institution : null,
    year: e.year ? String(e.year).trim() : null,
  }].filter((c) => c.name);
}

/** Ensure Key Highlights is in the template section list when payload has bullets
 * but the pinned template version predates that section. */
export function ensureKeyHighlightsSection(sections, resume) {
  const list = Array.isArray(sections) ? sections.map((s) => ({ ...s })) : [];
  if (list.some((s) => s?.key === 'key_highlights')) return list;
  const items = (resume?.key_highlights || []).filter((h) => typeof h === 'string' && h.trim());
  if (!items.length) return list;

  let summaryOrder = 1;
  const summary = list.find((s) => s?.key === 'summary');
  if (summary && summary.order != null) summaryOrder = Number(summary.order) || 1;

  for (const s of list) {
    const order = Number(s.order) || 0;
    if (order > summaryOrder) s.order = order + 1;
  }
  list.push({
    key: 'key_highlights',
    label: 'Key Highlights',
    enabled: true,
    order: summaryOrder + 1,
    type: 'list',
  });
  return list;
}

/** Pick certification (preferred) or highest education line — legacy helper. */
export function pickProfileCredential(companyResume) {
  const payload = companyResume?.payload;
  if (!payload) return null;

  const certs = Array.isArray(payload.certifications) ? payload.certifications : [];
  if (certs.length) {
    const sorted = [...certs].sort((a, b) => parseYear(b.year) - parseYear(a.year));
    const c = sorted[0];
    const parts = [c.name, c.issued_by].filter(Boolean);
    const label = parts.join(' · ');
    const year = c.year ? ` (${c.year})` : '';
    return { type: 'certification', label: `${label}${year}`.trim() };
  }

  const edu = Array.isArray(payload.education) ? payload.education : [];
  if (edu.length) {
    const sorted = [...edu].sort((a, b) => parseYear(b.year) - parseYear(a.year));
    const e = sorted[0];
    const parts = [e.degree, e.institution].filter(Boolean);
    const label = parts.join(' · ');
    const year = e.year ? ` (${e.year})` : '';
    return { type: 'education', label: `${label}${year}`.trim() };
  }

  return null;
}
